import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { client, registerUser, getRecaptchaConfig } from '../api/client';
import { useAppStore } from '../store/appStore';

// Declare grecaptcha Enterprise globally for TypeScript
declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

type UserType = 'fizicko_lice' | 'pravno_lice';

export function RegisterPage() {
  const navigate = useNavigate();
  const { setUser } = useAppStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [userType, setUserType] = useState<UserType>('fizicko_lice');
  const [maticniBroj, setMaticniBroj] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  // reCAPTCHA Enterprise state
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | null>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Ako je korisnik već prijavljen, preusmeri na početnu
  useEffect(() => {
    client.auth.getSession().then(session => {
      if (session.data?.user) {
        setUser({
          id: session.data.user.id,
          email: session.data.user.email,
          name: session.data.user.name || '',
        });
        navigate('/');
      }
    });
  }, []);

  // Fetch reCAPTCHA config
  useEffect(() => {
    getRecaptchaConfig().then(config => {
      setRecaptchaEnabled(config.enabled);
      setRecaptchaSiteKey(config.siteKey);
    });
  }, []);

  // Load reCAPTCHA Enterprise script
  useEffect(() => {
    if (!recaptchaEnabled || !recaptchaSiteKey) return;
    
    // Check if already loaded
    if (window.grecaptcha?.enterprise) {
      setRecaptchaLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
    };
    document.head.appendChild(script);
    
    return () => {
      // Don't remove script on unmount to prevent reloading issues
    };
  }, [recaptchaEnabled, recaptchaSiteKey]);

  const validateForm = (): string | null => {
    if (!acceptTerms) {
      return 'Morate prihvatiti Uslove korišćenja i Politiku privatnosti';
    }
    if (!firstName.trim() || !lastName.trim()) {
      return 'Ime i prezime su obavezni';
    }
    if (!email.trim()) {
      return 'Email adresa je obavezna';
    }
    if (!password) {
      return 'Lozinka je obavezna';
    }
    if (password !== passwordConfirm) {
      return 'Lozinke se ne poklapaju';
    }
    if (password.length < 8) {
      return 'Lozinka mora imati najmanje 8 karaktera';
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      return 'Lozinka mora sadržati velika slova, mala slova i brojeve';
    }
    if (userType === 'pravno_lice') {
      if (!maticniBroj || maticniBroj.length !== 8 || !/^\d+$/.test(maticniBroj)) {
        return 'Matični broj firme mora imati tačno 8 cifara';
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    // Email je case insensitive
    const normalizedEmail = email.toLowerCase().trim();
    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    
    // Get reCAPTCHA Enterprise token (invisible, runs in background)
    let recaptchaToken: string | undefined;
    if (recaptchaEnabled && recaptchaLoaded && recaptchaSiteKey && window.grecaptcha?.enterprise) {
      try {
        recaptchaToken = await new Promise<string>((resolve, reject) => {
          window.grecaptcha!.enterprise.ready(async () => {
            try {
              const token = await window.grecaptcha!.enterprise.execute(recaptchaSiteKey!, { action: 'register' });
              resolve(token);
            } catch (err) {
              reject(err);
            }
          });
        });
      } catch (err) {
        console.error('reCAPTCHA Enterprise error:', err);
        // Continue without token — backend will decide whether to allow
      }
    }

    try {
      // Koristi novu registerUser funkciju koja automatski kreira tokene
      const result = await registerUser(normalizedEmail, password, fullName, userType, recaptchaToken);

      if (result.success) {
        // Uspešna registracija
        const tokenAmount = userType === 'pravno_lice' ? 20 : 5;
        navigate('/prijava', {
          state: {
            message: result.needsVerification
              ? `Uspešno ste se registrovali! Proverite email za verifikaciju naloga. Nakon verifikacije dobijate ${tokenAmount} besplatnih tokena!`
              : `Uspešno ste se registrovali! Dobili ste ${tokenAmount} besplatnih tokena za početak.`
          }
        });
      } else {
        // Prikaži grešku, ali dozvoli navigaciju ako je moguće da je korisnik kreiran
        if (result.error?.includes('already exists') || result.error?.includes('već postoji')) {
          setError('Korisnik sa ovom email adresom već postoji. Prijavite se ili koristite drugu email adresu.');
        } else {
          // Moguće da je email slanje propalo ali korisnik je kreiran
          console.warn('Registration might have partially succeeded:', result.error);
          navigate('/prijava', {
            state: { message: 'Registracija je možda uspela. Pokušajte se prijaviti ili proverite email.' }
          });
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Došlo je do greške. Pokušajte ponovo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[420px]"
      >
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Kreiraj nalog
            </h1>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ime"
                className="w-full pl-12 pr-4 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="given-name"
              />
            </div>

            {/* Last Name */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Prezime"
                className="w-full pl-12 pr-4 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="family-name"
              />
            </div>

            {/* Email */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email adresa"
                className="w-full pl-12 pr-4 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Lozinka"
                className="w-full pl-12 pr-12 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {/* Password Confirm */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <input
                type={showPasswordConfirm ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Potvrdi lozinku"
                className="w-full pl-12 pr-12 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPasswordConfirm ? (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {/* User Type */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                Tip korisnika:
              </label>
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                  <input
                    type="radio"
                    name="userType"
                    value="fizicko_lice"
                    checked={userType === 'fizicko_lice'}
                    onChange={() => setUserType('fizicko_lice')}
                    className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                  />
                  <span>Fizičko lice</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                  <input
                    type="radio"
                    name="userType"
                    value="pravno_lice"
                    checked={userType === 'pravno_lice'}
                    onChange={() => setUserType('pravno_lice')}
                    className="w-4 h-4 text-amber-500 focus:ring-amber-500"
                  />
                  <span>Pravno lice</span>
                </label>
              </div>
            </div>

            {/* Matični broj (for pravno lice) */}
            {userType === 'pravno_lice' && (
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 21h18" />
                    <path d="M9 21V7l-4 2v12" />
                    <path d="M19 21V11l-4-2v12" />
                    <path d="M9 7l6-3v14" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={maticniBroj}
                  onChange={(e) => setMaticniBroj(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Matični broj firme (8 cifara)"
                  maxLength={8}
                  className="w-full pl-12 pr-4 py-3.5 border-0 border-b-2 border-slate-200 dark:border-slate-600 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>
            )}

            {/* Terms */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer text-sm text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                />
                <span className="leading-relaxed">
                  Prihvatam{' '}
                  <Link to="/uslovi-koriscenja" target="_blank" className="text-amber-500 hover:underline">
                    Uslove korišćenja
                  </Link>
                  {' '}i{' '}
                  <Link to="/politika-privatnosti" target="_blank" className="text-amber-500 hover:underline">
                    Politiku privatnosti
                  </Link>
                  .
                </span>
              </label>
            </div>

            {/* reCAPTCHA */}
            {recaptchaEnabled && recaptchaSiteKey && (
              <div className="pt-2 flex justify-center">
                {/* reCAPTCHA Enterprise v3 - invisible, no widget needed */}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all transform hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mt-6"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Registracija...
                </span>
              ) : (
                'Kreiraj nalog'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Već imate nalog?{' '}
              <Link to="/prijava" className="text-amber-500 hover:text-amber-600 font-semibold hover:underline">
                Ulogujte se
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
